import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { arcadeApi, arcadeAuth } from '../../services/arcade';
import { RegisterGate } from '../../components/arcade/RegisterGate';
import { MiniLineChart } from '../../components/tools/MiniLineChart';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

export const CandleChallengePage: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(arcadeAuth.isLoggedIn());
  const [challenge, setChallenge] = useState<any | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    arcadeApi
      .candleToday()
      .then((data) => {
        setChallenge(data);
        if (data.attempt) {
          setResult({
            correct: data.attempt.correct,
            correctIndex: data.correctIndex,
            explanation: data.explanation,
            points: 0,
          });
          setSelected(data.attempt.answerIndex);
        }
      })
      .catch(() => setChallenge({ available: false }))
      .finally(() => setLoading(false));
  }, [loggedIn]);

  const submit = async () => {
    if (selected === null) return;
    setError(null);
    try {
      const data = await arcadeApi.candleSubmit(selected);
      setResult(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao enviar resposta.');
    }
  };

  const chartValues = Array.isArray(challenge?.chartData)
    ? challenge.chartData.map((point: any) => point.v)
    : [];
  const chartLabels = Array.isArray(challenge?.chartData)
    ? challenge.chartData.map((point: any) => point.t)
    : [];

  return (
    <>
      <Helmet>
        <title>Candle Challenge: Leia o Gráfico | Crypto Arcade | {SITE_NAME}</title>
        <meta
          name="description"
          content="Analise um gráfico histórico já encerrado e identifique o padrão ou conceito técnico. Jogo educativo diário — sem apostas e sem previsão de preços."
        />
        <link rel="canonical" href={`${SITE_URL}/arcade/candle-challenge`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <Link to="/arcade" className="hover:text-blue-600 transition">Arcade</Link>
            <span>›</span>
            <span className="text-gray-700">Candle Challenge</span>
          </nav>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">📉 Candle Challenge</h1>
          <p className="text-gray-600 mb-6">
            Um gráfico histórico <strong>já encerrado</strong>, uma pergunta sobre o padrão ou conceito que ele
            ilustra. Aqui ninguém aposta em subida ou queda — o desafio é ler o gráfico.
          </p>

          {!loggedIn ? (
            <RegisterGate onRegistered={() => setLoggedIn(true)} />
          ) : loading ? (
            <div className="text-gray-500 text-center py-10">Carregando desafio...</div>
          ) : !challenge?.available ? (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-600">
              O desafio de hoje ainda está sendo preparado. Volte em alguns minutos!
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow p-6 md:p-8">
              <p className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-2">
                {challenge.asset} · {challenge.periodLabel} · dados históricos (USD)
              </p>

              {chartValues.length > 1 && (
                <div className="mb-5">
                  <MiniLineChart
                    series={[{ label: challenge.asset, values: chartValues, color: '#2563eb' }]}
                    xLabels={chartLabels}
                    formatValue={(v) => `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                  />
                </div>
              )}

              <p className="font-semibold text-gray-900 mb-3">{challenge.question}</p>
              <div className="space-y-2 mb-5">
                {challenge.options.map((option: string, index: number) => {
                  let className = 'border-gray-200 hover:border-blue-300 text-gray-700';
                  if (result) {
                    if (index === result.correctIndex) className = 'border-green-500 bg-green-50 text-green-900 font-semibold';
                    else if (index === selected) className = 'border-red-400 bg-red-50 text-red-800';
                    else className = 'border-gray-200 text-gray-400';
                  } else if (selected === index) {
                    className = 'border-blue-500 bg-blue-50 text-blue-900 font-semibold';
                  }
                  return (
                    <button
                      key={index}
                      onClick={() => !result && setSelected(index)}
                      disabled={Boolean(result)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition ${className}`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>

              {result ? (
                <div className="text-center">
                  <p className="text-3xl mb-1">{result.correct ? '🎯' : '📚'}</p>
                  <p className="font-bold text-gray-900 mb-2">
                    {result.correct ? 'Resposta correta!' : 'Resposta incorreta.'}
                    {result.points > 0 && <span className="text-green-700"> +{result.points} NC</span>}
                  </p>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-left text-sm text-blue-900 leading-relaxed mb-4">
                    📚 {result.explanation}
                  </div>
                  <Link to="/arcade" className="text-blue-600 hover:underline text-sm font-semibold">
                    ← Voltar ao Arcade
                  </Link>
                </div>
              ) : (
                <button
                  onClick={submit}
                  disabled={selected === null}
                  className="w-full bg-blue-600 text-white font-semibold rounded-lg px-6 py-3 hover:bg-blue-700 transition disabled:opacity-50"
                >
                  Confirmar resposta
                </button>
              )}
              {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

              <p className="text-xs text-gray-400 mt-5">
                Dados históricos via CoinGecko. Jogo educativo — não é análise nem recomendação de investimento.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
